/**
 * Module: web/src/stores/settings.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.1.0
 * Date: 2026-07-08
 *
 * Theme + brand store (pattern: firstline-insight stores/settings.ts).
 * Theme persists to localStorage + <html data-theme>. Brand strings are the
 * portal identity. NO auth data lives here (auth store is in-memory only).
 *
 * v1.1.0 (2026-07-19): brandLogo -- the horizontal "Commercial Towing"
 * one-line badge (Robert's asset) now IS the portal identity in the header +
 * login hero, replacing the wordmark/subline text. The strings stay exported:
 * brandWordmark backs document.title fallbacks + the logo's alt text;
 * brandSubline stays the accessible company name. The logo is a bundler-hashed
 * URL (the leaflet-marker asset pattern); its thick navy outline carries
 * legibility on the light-theme card (#f6f8fa) while the white/red fills carry
 * it on dark (#161b22), so one asset serves both themes.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import brandLogoUrl from '@/assets/branding/commercial-towing-one-line.png';

export type Theme = 'dark' | 'light';

const THEME_KEY = 'ops-theme';

export const useSettingsStore = defineStore('settings', () => {
  const theme = ref<Theme>((localStorage.getItem(THEME_KEY) as Theme | null) ?? 'dark');
  const brandWordmark = ref('CTS Operations Portal');
  const brandSubline = ref('Commercial Towing Road Solutions');
  const brandLogo = ref(brandLogoUrl);

  const isDark = computed(() => theme.value === 'dark');

  function setTheme(t: Theme) {
    theme.value = t;
    localStorage.setItem(THEME_KEY, t);
    document.documentElement.setAttribute('data-theme', t);
  }

  function toggleTheme() {
    setTheme(theme.value === 'dark' ? 'light' : 'dark');
  }

  function applyThemeAttribute() {
    document.documentElement.setAttribute('data-theme', theme.value);
  }

  return { theme, brandWordmark, brandSubline, brandLogo, isDark, setTheme, toggleTheme, applyThemeAttribute };
});
